import { type KeyboardEvent, type ReactNode, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Close, Search } from "../icons/icons";
import { cx } from "../lib/cx";
import { useUiStrings } from "../strings/ui-strings";

export interface SelectOption {
	value: string;
	label: string;
	/** Shown in full in the hint bar under the list while the option is hovered/highlighted. */
	description?: string;
	disabled?: boolean;
	/** Options sharing a group render under a small heading, in first-seen order. */
	group?: string;
}

interface BaseProps {
	options: ReadonlyArray<SelectOption>;
	placeholder?: string;
	/**
	 * Adds a search box at the top of the list; filters by label (and description).
	 * On by default for every Select (platform rule) — pass false to opt out.
	 */
	searchable?: boolean;
	searchPlaceholder?: string;
	/** Shows an × on the trigger that empties the value. */
	clearable?: boolean;
	disabled?: boolean;
	/**
	 * `md` (default) is the designer's 48px control (`.pick` / `.sel`: 15.5px
	 * text, 14px padding) for forms and filter rows; `sm` is her 40px small
	 * control for dense toolbars.
	 */
	size?: "sm" | "md";
	/** What to say when no option matches the search. */
	emptyMessage?: ReactNode;
	id?: string;
	name?: string;
	"aria-label"?: string;
	className?: string;
	/**
	 * Per-instance overrides for the built-in chrome. Usually unnecessary: the
	 * defaults come from `<UiStringsProvider>` (English without one).
	 */
	labels?: SelectLabels;
}

export interface SelectLabels {
	/** aria-label on the × that empties the value, and the multi-select footer's Clear button. */
	clear?: string;
	/** aria-label for a chip's remove ×. */
	removeOption?: (label: string) => string;
	/** Footer count in multi mode ("3 selected"). */
	selectedCount?: (count: number) => string;
	selectAll?: string;
}

interface SingleProps extends BaseProps {
	multiple?: false;
	value: string | null;
	onChange: (value: string | null) => void;
}

interface MultiProps extends BaseProps {
	multiple: true;
	value: ReadonlyArray<string>;
	onChange: (value: string[]) => void;
	/** Chips shown on the trigger before collapsing to "+N". Default 2. */
	maxChips?: number;
}

export type SelectProps = SingleProps | MultiProps;

/**
 * Select — the platform dropdown. A button trigger styled like a daisyUI
 * field, and a listbox popover with optional search and multi-select.
 * Built by hand because daisyUI's `select` is the native element (no search,
 * no chips) and the native multiple-select is unusable on a desk.
 *
 * Controlled only: pass `value` + `onChange`. Keyboard: ↑/↓ move, Enter
 * picks, Esc closes, typing filters when searchable.
 */
export function Select(props: SelectProps) {
	const { select: strings, formatNumber } = useUiStrings();
	const {
		options,
		placeholder = strings.placeholder,
		searchable = true,
		searchPlaceholder = strings.searchPlaceholder,
		clearable = false,
		disabled = false,
		size = "md",
		emptyMessage = strings.noMatches,
		className,
		labels,
	} = props;
	const clearLabel = labels?.clear ?? strings.clear;
	const removeOptionLabel = labels?.removeOption ?? strings.removeOption;
	const selectedCountLabel = labels?.selectedCount ?? strings.selectedCount;
	const selectAllLabel = labels?.selectAll ?? strings.selectAll;
	const multiple = props.multiple === true;
	const selectedValues = useMemo<ReadonlyArray<string>>(
		() => (props.multiple ? props.value : props.value === null ? [] : [props.value]),
		[props.multiple, props.value],
	);

	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [active, setActive] = useState(0);
	const root = useRef<HTMLDivElement>(null);
	const popover = useRef<HTMLDivElement>(null);
	const searchRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLUListElement>(null);
	const listId = useId();

	/**
	 * The popover renders through a portal with fixed positioning, so no
	 * ancestor's overflow can clip it — a Select inside a Modal's scrolling
	 * body used to open INSIDE the scroll container and force the user to
	 * scroll the dialog to see the options.
	 *
	 * The portal target matters: Modal is a native <dialog> in the browser's
	 * top layer, and the top layer paints over everything portalled to
	 * document.body. So the popover mounts into the nearest open <dialog>
	 * when there is one, and only otherwise into the body.
	 */
	const [pos, setPos] = useState<{ top: number; left: number; width: number; up: boolean } | null>(null);
	useLayoutEffect(() => {
		if (!open) {
			setPos(null);
			return;
		}
		const place = () => {
			const r = root.current?.getBoundingClientRect();
			if (!r) return;
			// The popover is at most ~22rem tall (search + 16rem list + hint bar).
			const spaceBelow = window.innerHeight - r.bottom;
			const up = spaceBelow < 360 && r.top > spaceBelow;
			setPos({ top: up ? r.top : r.bottom, left: r.left, width: r.width, up });
		};
		place();
		// capture: true also catches the Modal body (or any panel) scrolling under us.
		window.addEventListener("resize", place);
		document.addEventListener("scroll", place, true);
		return () => {
			window.removeEventListener("resize", place);
			document.removeEventListener("scroll", place, true);
		};
	}, [open]);

	const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);

	const visible = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return options;
		return options.filter(
			(o) => o.label.toLowerCase().includes(q) || (o.description?.toLowerCase().includes(q) ?? false),
		);
	}, [options, query]);

	/* open / close housekeeping */
	useEffect(() => {
		if (!open) return;
		setQuery("");
		setActive(Math.max(0, visible.findIndex((o) => selectedValues.includes(o.value))));
		const t = setTimeout(() => searchRef.current?.focus(), 0);
		const onDown = (e: MouseEvent) => {
			const t = e.target as Node;
			// The popover lives in a portal, so "outside" means outside BOTH the trigger and it.
			if (root.current?.contains(t) || popover.current?.contains(t)) return;
			setOpen(false);
		};
		document.addEventListener("mousedown", onDown);
		return () => {
			clearTimeout(t);
			document.removeEventListener("mousedown", onDown);
		};
		// Intentionally only on open/close — the initial highlight should not jump while searching.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	useEffect(() => {
		setActive(0);
	}, [query]);

	// Keep the active option scrolled into view.
	useEffect(() => {
		if (!open) return;
		const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
		el?.scrollIntoView({ block: "nearest" });
	}, [active, open]);

	const commit = (next: ReadonlyArray<string>) => {
		if (props.multiple) props.onChange([...next]);
		else props.onChange(next[0] ?? null);
	};

	const pick = (opt: SelectOption) => {
		if (opt.disabled) return;
		if (multiple) {
			commit(
				selectedValues.includes(opt.value)
					? selectedValues.filter((v) => v !== opt.value)
					: [...selectedValues, opt.value],
			);
		} else {
			commit(selectedValues[0] === opt.value && clearable ? [] : [opt.value]);
			setOpen(false);
		}
	};

	const onKeyDown = (e: KeyboardEvent) => {
		if (!open) {
			if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
				e.preventDefault();
				setOpen(true);
			}
			return;
		}
		switch (e.key) {
			case "ArrowDown":
				e.preventDefault();
				setActive((i) => Math.min(visible.length - 1, i + 1));
				break;
			case "ArrowUp":
				e.preventDefault();
				setActive((i) => Math.max(0, i - 1));
				break;
			case "Home":
				e.preventDefault();
				setActive(0);
				break;
			case "End":
				e.preventDefault();
				setActive(visible.length - 1);
				break;
			case "Enter": {
				e.preventDefault();
				const opt = visible[active];
				if (opt) pick(opt);
				break;
			}
			case "Escape":
				e.preventDefault();
				setOpen(false);
				break;
			case "Tab":
				setOpen(false);
				break;
		}
	};

	/* ---------- trigger content ---------- */
	const selectedOpts = selectedValues.map((v) => byValue.get(v)).filter((o): o is SelectOption => !!o);
	const maxChips = props.multiple ? (props.maxChips ?? 2) : 0;
	const hasValue = selectedOpts.length > 0;

	let display: ReactNode;
	if (!hasValue) {
		display = <span className={cx("block truncate", disabled ? "text-gms-ink-3" : "text-[#9a9cb2]")}>{placeholder}</span>;
	} else if (multiple) {
		const shown = selectedOpts.slice(0, maxChips);
		const rest = selectedOpts.length - shown.length;
		display = (
			<span className="flex min-w-0 flex-wrap items-center gap-[0.25rem]">
				{shown.map((o) => (
					<span
						key={o.value}
						className="inline-flex max-w-[10rem] items-center gap-[0.125rem] rounded-[0.25rem] bg-base-200 px-[0.375rem] py-[0.0625rem] text-[0.75rem] font-medium"
					>
						<span className="truncate">{o.label}</span>
						<span
							role="button"
							tabIndex={-1}
							aria-label={removeOptionLabel(o.label)}
							className="ml-[0.125rem] rounded-full p-[0.0625rem] text-base-content/50 hover:bg-base-300 hover:text-base-content"
							onMouseDown={(e) => e.stopPropagation()}
							onClick={(e) => {
								e.stopPropagation();
								commit(selectedValues.filter((v) => v !== o.value));
							}}
						>
							<Close className="size-[0.75rem]" />
						</span>
					</span>
				))}
				{rest > 0 && <span className="text-[0.75rem] font-medium text-base-content/60">+{formatNumber(rest)}</span>}
			</span>
		);
	} else {
		display = <span className="block truncate">{selectedOpts[0]!.label}</span>;
	}

	const groups = useMemo(() => {
		const order: string[] = [];
		for (const o of visible) {
			const g = o.group ?? "";
			if (!order.includes(g)) order.push(g);
		}
		return order;
	}, [visible]);

	let flatIndex = -1;

	/*
	 * ONE keyboard handler, on the wrapper. The popover is portalled, but React
	 * bubbles synthetic events along the React tree, not the DOM tree — so a key
	 * pressed in the popover's search box still reaches this div. A second
	 * `onKeyDown` on the popover ran the handler twice per press (↓ skipped an
	 * option). With `searchable={false}` focus stays on the trigger, which is
	 * inside the wrapper, so the same handler serves both cases.
	 */
	return (
		<div ref={root} className={cx("relative min-w-0", className)} onKeyDown={onKeyDown}>
			<button
				type="button"
				id={props.id}
				disabled={disabled}
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-controls={listId}
				aria-label={props["aria-label"]}
				onClick={() => setOpen((o) => !o)}
				className={cx(
					// Her `.pick`: --line-2 border that turns navy on hover/focus with a soft 3px ring.
					"flex w-full min-w-0 cursor-pointer items-center gap-[0.625rem] rounded-[0.5rem] border border-gms-line-2 bg-base-100 text-left outline-none transition-[border-color,box-shadow]",
					"hover:border-gms-navy focus-visible:border-gms-navy focus-visible:shadow-[0_0_0_3px_rgb(46_49_146/0.12)]",
					open && "border-gms-navy shadow-[0_0_0_3px_rgb(46_49_146/0.12)]",
					disabled && "cursor-not-allowed border-gms-line bg-base-200 hover:border-gms-line",
					size === "sm"
						? "min-h-[2.5rem] pr-[0.625rem] pl-[0.75rem] text-[0.84375rem]"
						: "min-h-[3rem] pr-[0.75rem] pl-[0.875rem] text-[0.96875rem]",
					"py-[0.25rem]",
				)}
			>
				{/*
				 * The label never sizes the field: `contain: inline-size` gives it no intrinsic
				 * width, so a long placeholder or option ellipsises inside the container instead
				 * of pushing the field (and the page) wider — in a grid / flex parent without
				 * min-w-0 too.
				 */}
				<span className="block min-w-0 flex-1 overflow-hidden [contain:inline-size]">{display}</span>
				{clearable && hasValue && !disabled && (
					<span
						role="button"
						tabIndex={-1}
						aria-label={clearLabel}
						className="-mr-[0.125rem] rounded-full p-[0.125rem] text-base-content/45 hover:bg-base-200 hover:text-base-content"
						onMouseDown={(e) => e.stopPropagation()}
						onClick={(e) => {
							e.stopPropagation();
							commit([]);
						}}
					>
						<Close className="size-[0.875rem]" />
					</span>
				)}
				<ChevronDown
					className={cx("size-[1.125rem] shrink-0 text-gms-ink-3 transition-transform", open && "rotate-180")}
				/>
			</button>
			{props.name && <input type="hidden" name={props.name} value={selectedValues.join(",")} />}

			{open &&
				pos &&
				createPortal(
					<div
						ref={popover}
						style={{
							left: pos.left,
							width: Math.max(pos.width, 192),
							...(pos.up ? { bottom: window.innerHeight - pos.top + 4 } : { top: pos.top + 4 }),
						}}
						className="fixed z-30 flex flex-col overflow-hidden rounded-box border border-base-300 bg-base-100 shadow-[0_8px_24px_rgba(16,24,40,0.12)]"
					>
					{searchable && (
						<label className="flex items-center gap-[0.5rem] border-b border-base-300 px-[0.625rem]">
							<Search className="size-[0.875rem] shrink-0 text-base-content/45" />
							<input
								ref={searchRef}
								type="text"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								placeholder={searchPlaceholder}
								aria-label={searchPlaceholder}
								aria-controls={listId}
								aria-activedescendant={visible[active] ? `${listId}-${active}` : undefined}
								className="min-w-0 flex-1 bg-transparent py-[0.625rem] text-[0.875rem] outline-none placeholder:text-[#9a9cb2]"
							/>
						</label>
					)}

					<ul
						ref={listRef}
						id={listId}
						role="listbox"
						aria-multiselectable={multiple || undefined}
						className="max-h-[16rem] overflow-y-auto p-[0.25rem] [scrollbar-width:thin]"
					>
						{visible.length === 0 && (
							<li className="px-[0.625rem] py-[0.75rem] text-center text-[0.8125rem] text-base-content/50">{emptyMessage}</li>
						)}
						{groups.map((g) => (
							<li key={g || "__none"} role="presentation">
								{g && (
									<p className="px-[0.625rem] pt-[0.5rem] pb-[0.25rem] text-[0.625rem] font-semibold tracking-[0.08em] text-base-content/50 uppercase">
										{g}
									</p>
								)}
								<ul role="group" aria-label={g || undefined}>
									{visible
										.filter((o) => (o.group ?? "") === g)
										.map((o) => {
											flatIndex += 1;
											const i = flatIndex;
											const isSel = selectedValues.includes(o.value);
											const isActive = i === active;
											return (
												<li
													key={o.value}
													id={`${listId}-${i}`}
													role="option"
													aria-selected={isSel}
													aria-disabled={o.disabled || undefined}
													data-index={i}
													onMouseEnter={() => setActive(i)}
													onMouseDown={(e) => e.preventDefault()}
													onClick={() => pick(o)}
													className={cx(
														"flex cursor-pointer items-center gap-[0.5rem] rounded-[0.375rem] px-[0.625rem] py-[0.5rem] text-[0.875rem]",
														isActive && "bg-base-200",
														o.disabled && "cursor-not-allowed opacity-40",
													)}
												>
													{multiple ? (
														<span
															aria-hidden
															className={cx(
																"flex size-[1rem] shrink-0 items-center justify-center rounded-[0.25rem] border",
																isSel ? "border-primary bg-primary text-primary-content" : "border-base-content/30 bg-base-100",
															)}
														>
															{isSel && <Check className="size-[0.75rem]" />}
														</span>
													) : null}
													<span className={cx("min-w-0 flex-1 truncate", isSel && !multiple && "font-medium")}>
														{o.label}
													</span>
													{!multiple && isSel && <Check className="size-[1rem] shrink-0 text-primary" />}
												</li>
											);
										})}
								</ul>
							</li>
						))}
					</ul>

					{/* The popover clips absolutely-positioned tooltips (overflow + scroll), so the
					    hovered/highlighted option's description shows here in full instead. */}
					{visible[active]?.description && (
						<p className="border-t border-base-300 bg-base-200/50 px-[0.625rem] py-[0.375rem] text-[0.75rem] leading-snug text-base-content/70">
							{visible[active]!.description}
						</p>
					)}

					{multiple && (
						<div className="flex items-center justify-between border-t border-base-300 px-[0.625rem] py-[0.375rem] text-[0.75rem] text-base-content/60">
							<span className="tabular-nums">
								{selectedCountLabel(selectedValues.length)}
							</span>
							<div className="flex gap-[0.5rem]">
								<button
									type="button"
									className="link link-hover"
									onClick={() =>
										commit([
											...new Set([...selectedValues, ...visible.filter((o) => !o.disabled).map((o) => o.value)]),
										])
									}
								>
									{selectAllLabel}
								</button>
								<button type="button" className="link link-hover" disabled={!hasValue} onClick={() => commit([])}>
									{clearLabel}
								</button>
							</div>
						</div>
					)}
					</div>,
					root.current?.closest("dialog") ?? document.body,
				)}
		</div>
	);
}
